import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Button,
  Chip,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';

import { useQuery } from '@tanstack/react-query';

import { getMultiplexPanels, getMultiplexPanelsQueryKey } from 'api/multiplexPanels';

import {
  getNormalizationSummary,
  SlideChannelNormalizationResultSummary,
} from 'api/slideMultiplexChannelNormalizations';

import { useTrackActionFormDirty } from 'components/ActionDialog/actionFormDirtyState';
import { useConfirmation } from 'components/modals/ConfirmationContext';
import { MultiplexPanelView } from 'interfaces/multiplexPanels';

import { every, filter, find, flatMap, map, sumBy, uniq } from 'lodash';

import { default as React, useRef, useState } from 'react';

import { CasesParams } from 'utils/useCasesParams/types';

import { Permission } from 'interfaces/permissionOption';
import { usePermissions } from 'utils/usePermissions';
import { DefineMultiplexPanelsDialog } from '../DefineMultiplexPanelsDialog';
import { useMultiplexActionSelection } from '../Multiplex/useMultiplexActionSelection';

import { getCurrentMarkerRangeDetails } from './bulkMultiplexNormalizationForm';

import { PanelNormalizationControls, PanelNormalizationForm } from './PanelNormalizationForm';

interface Props {
  casesParams: CasesParams;
  onClose: (event?: unknown, force?: boolean) => void;
  queryStudyMultiplexSlides?: boolean;
}

export const getPanelNormalizationStatus = (
  panel: MultiplexPanelView,
  summaries: SlideChannelNormalizationResultSummary[]
) => {
  let missingChannelCount = 0;
  let normalizedChannelCount = 0;
  let missingMarkerNameCount = 0;
  for (const slide of panel.slides) {
    const summary = find(summaries, { slideId: slide.slideId });
    for (const channelId of Object.keys(slide.channelMapping)) {
      const markerType = slide.channelMarkerTypes[channelId];
      if (!markerType?.trim()) {
        missingMarkerNameCount += 1;
        continue;
      }
      const normalization = summary?.normalizationsSummary[channelId];
      const params = normalization?.channelNormalizationResults?.params;
      if (
        markerType &&
        normalization?.markerType === markerType &&
        Number.isFinite(params?.min_c) &&
        Number.isFinite(params?.max_c) &&
        params.min_c < params.max_c
      )
        normalizedChannelCount += 1;
      else missingChannelCount += 1;
    }
  }
  const panelSummaries = filter(summaries, (summary) =>
    panel.slides.some((slide) => slide.slideId === summary.slideId)
  );
  const markerIds = uniq(flatMap(panel.slides, (slide) => Object.values(slide.channelMarkerTypes)));
  const mixedMarkerIds = filter(
    markerIds,
    (markerId) => getCurrentMarkerRangeDetails(panelSummaries, markerId).status === 'mixed'
  );
  return { missingChannelCount, normalizedChannelCount, missingMarkerNameCount, mixedMarkerIds };
};

export const BulkMultiplexNormalizationDialog = ({
  casesParams,
  onClose,
  queryStudyMultiplexSlides = false,
}: Props) => {
  const selection = useMultiplexActionSelection(casesParams, { enabled: !queryStudyMultiplexSlides });
  const { hasPermission } = usePermissions();
  const canOpenMarkerNamingDialog = hasPermission(Permission.EditQCMarkers);
  const confirmation = useConfirmation();
  const panelControls = useRef<Record<string, PanelNormalizationControls | null>>({});
  const panelRefCallbacks = useRef<Record<string, (controls: PanelNormalizationControls | null) => void>>({});
  const [panelStates, setPanelStates] = useState<
    Record<string, Pick<PanelNormalizationControls, 'pendingCount' | 'canSave' | 'isSaving'>>
  >({});
  const [isSavingRanges, setIsSavingRanges] = useState(false);
  const saveLock = useRef(false);
  const getPanelRef = (panelId: string) => {
    if (!panelRefCallbacks.current[panelId])
      panelRefCallbacks.current[panelId] = (controls) => {
        panelControls.current[panelId] = controls;
        if (!controls) return;
        const { pendingCount, canSave, isSaving } = controls;
        setPanelStates((current) => {
          const previous = current[panelId];
          return previous?.pendingCount === pendingCount &&
            previous.canSave === canSave &&
            previous.isSaving === isSaving
            ? current
            : { ...current, [panelId]: { pendingCount, canSave, isSaving } };
        });
      };
    return panelRefCallbacks.current[panelId];
  };
  const [panelDefinitionParams, setPanelDefinitionParams] = useState<{ slideId?: string } | null>(null);
  const openMarkerNamingDialog = (params: { slideId?: string } = {}) => {
    if (canOpenMarkerNamingDialog) setPanelDefinitionParams(params);
  };
  const scope = queryStudyMultiplexSlides ? casesParams : selection.effectiveCasesParams || casesParams;
  const studyId = scope.studyId || scope.filters?.studyId;
  const [expandedPanels, setExpandedPanels] = useState<Record<string, boolean>>({});
  const panelsQuery = useQuery({
    queryKey: getMultiplexPanelsQueryKey(scope),
    queryFn: ({ signal }) => getMultiplexPanels(scope, signal),
    enabled:
      Boolean(studyId) && (queryStudyMultiplexSlides || !selection.isLoadingSelection) && !selection.blockingMessage,
  });
  const panels = panelsQuery.data?.panels || [];
  const pendingPanels = filter(panels, (panel) => (panelStates[panel.panelId]?.pendingCount || 0) > 0);
  const pendingRangeCount = sumBy(pendingPanels, (panel) => panelStates[panel.panelId].pendingCount);
  useTrackActionFormDirty(pendingRangeCount > 0);
  const canSaveRanges =
    pendingPanels.length > 0 &&
    every(pendingPanels, (panel) => panelStates[panel.panelId].canSave && !panelStates[panel.panelId].isSaving) &&
    !isSavingRanges;
  const saveRangeChanges = async () => {
    if (!canSaveRanges || saveLock.current) return;
    saveLock.current = true;
    setIsSavingRanges(true);
    try {
      for (const panel of pendingPanels) {
        if (!(await panelControls.current[panel.panelId]?.save())) break;
      }
    } finally {
      saveLock.current = false;
      setIsSavingRanges(false);
    }
  };
  const discardRangeChanges = async () => {
    if (
      !(await confirmation({
        title: 'Discard changes',
        text: 'Discard all unsaved normalization range changes?',
        confirmButtonProps: { title: 'Discard changes', variant: 'contained', color: 'warning' },
        cancelButtonProps: { color: 'inherit' },
      }))
    )
      return false;
    for (const panel of pendingPanels) panelControls.current[panel.panelId]?.discard();
    return true;
  };
  const slideIds = uniq(map(flatMap(panels, 'slides'), 'slideId'));
  const normalizationScope: CasesParams = {
    ...scope,
    slidesMode: true,
    slideIdsToInclude: slideIds,
    slideIdsToExclude: undefined,
    caseIdsToInclude: undefined,
    caseIdsToExclude: undefined,
  };
  const normalizationQuery = useQuery({
    queryKey: ['bulkMultiplexNormalizationSummary', normalizationScope],
    queryFn: ({ signal }) => getNormalizationSummary(normalizationScope, signal),
    enabled: slideIds.length > 0,
  });
  if (!queryStudyMultiplexSlides && selection.blockingMessage)
    return <Alert severity="error">{selection.blockingMessage}</Alert>;
  if (panelsQuery.isLoading || (!queryStudyMultiplexSlides && selection.isLoadingSelection))
    return (
      <Stack spacing={1}>
        <Skeleton variant="rectangular" height={160} />
        <Typography role="status">Loading panel information across the cohort…</Typography>
      </Stack>
    );
  return (
    <Stack spacing={2}>
      <Typography color="text.secondary">
        Normalization is required for every panel. DNA and mIF ranges are used for positivity classification; RNA ranges
        control visualization.
      </Typography>
      {panelsQuery.isError ? (
        <Alert severity="error">
          Panel information could not be loaded. Retry before changing normalization ranges.
        </Alert>
      ) : null}
      {normalizationQuery.isError && (
        <Alert
          severity="error"
          action={
            <Button color="inherit" onClick={() => normalizationQuery.refetch()}>
              Retry
            </Button>
          }
        >
          Current normalization ranges could not be loaded. Retry before applying changes.
        </Alert>
      )}
      {!panels.length ? (
        <Alert severity="info">
          Define multiplex panels before normalizing these slides.
          {canOpenMarkerNamingDialog && (
            <Button variant="contained" color="secondary" onClick={() => openMarkerNamingDialog({})}>
              Define multiplex panels
            </Button>
          )}
        </Alert>
      ) : (
        map(panels, (panel) => {
          const expanded = expandedPanels[panel.panelId] ?? panel.panelId === panels[0].panelId;
          const status = getPanelNormalizationStatus(panel, normalizationQuery.data || []);
          const unnamedCandidates = filter(panel.candidateSlides || [], (slide) =>
            Object.keys(slide.channelMapping).some((channelId) => !slide.channelMarkerTypes[channelId]?.trim())
          );
          const panelScope: CasesParams = {
            ...scope,
            slidesMode: true,
            slideIdsToInclude: map(panel.slides, 'slideId'),
            slideIdsToExclude: undefined,
            caseIdsToInclude: undefined,
            caseIdsToExclude: undefined,
          };
          return (
            <Accordion
              key={panel.panelId}
              expanded={expanded}
              onChange={(_event, isExpanded) =>
                setExpandedPanels((current) => ({ ...current, [panel.panelId]: isExpanded }))
              }
              disableGutters
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" alignItems="center">
                  <Typography variant="subtitle1">{panel.name || panel.panelId.replace('panel-', 'Panel ')}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {panel.channels.length} {panel.channels.length === 1 ? 'channel' : 'channels'} ·{' '}
                    {panel.slides.length} {panel.slides.length === 1 ? 'slide' : 'slides'}
                  </Typography>
                  <Chip
                    size="small"
                    color={
                      status.missingMarkerNameCount
                        ? 'default'
                        : normalizationQuery.isLoading
                        ? 'default'
                        : status.missingChannelCount
                        ? 'warning'
                        : 'success'
                    }
                    label={
                      status.missingMarkerNameCount
                        ? 'Marker names required'
                        : normalizationQuery.isLoading
                        ? 'Checking normalization'
                        : status.missingChannelCount
                        ? `${status.missingChannelCount} ${
                            status.missingChannelCount === 1 ? 'range' : 'ranges'
                          } required`
                        : 'Normalization complete'
                    }
                  />
                  {status.mixedMarkerIds.length > 0 && (
                    <Chip
                      size="small"
                      label={`${status.mixedMarkerIds.length} ${
                        status.mixedMarkerIds.length === 1 ? 'channel has' : 'channels have'
                      } different ranges`}
                    />
                  )}
                </Stack>
              </AccordionSummary>
              <AccordionDetails>
                {panel.slides.length > 0 ? (
                  <PanelNormalizationForm
                    ref={getPanelRef(panel.panelId)}
                    disabled={isSavingRanges}
                    panel={panel}
                    casesParams={panelScope}
                    enabled={expanded}
                    normalizationSummaries={filter(normalizationQuery.data || [], (summary) =>
                      panel.slides.some((slide) => slide.slideId === summary.slideId)
                    )}
                    isLoadingNormalization={normalizationQuery.isLoading}
                    normalizationAvailable={!normalizationQuery.isError && !panelsQuery.isError}
                    onClose={onClose}
                    canOpenMarkerNamingDialog={canOpenMarkerNamingDialog}
                    openMarkerNamingDialog={openMarkerNamingDialog}
                  />
                ) : (
                  <Alert severity="info">Associate compatible slides before normalizing this panel.</Alert>
                )}
                {(unnamedCandidates.length > 0 || !panel.slides.length) && (
                  <Stack spacing={1} sx={{ mt: 2 }}>
                    {unnamedCandidates.length > 0 && (
                      <Typography variant="body2">
                        {unnamedCandidates.length} compatible{' '}
                        {unnamedCandidates.length === 1 ? 'slide requires' : 'slides require'} marker names first.
                      </Typography>
                    )}
                    {canOpenMarkerNamingDialog ? (
                      <Button
                        variant="contained"
                        color="secondary"
                        sx={{ alignSelf: 'flex-start' }}
                        onClick={() =>
                          openMarkerNamingDialog(
                            unnamedCandidates.length === 1 ? { slideId: unnamedCandidates[0].slideId } : {}
                          )
                        }
                      >
                        Define multiplex panels
                      </Button>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        Ask a study editor to define this panel&apos;s marker names.
                      </Typography>
                    )}
                  </Stack>
                )}
              </AccordionDetails>
            </Accordion>
          );
        })
      )}
      <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end">
        <Typography variant="body2" color="text.secondary" sx={{ mr: 'auto' }} role="status">
          {pendingRangeCount
            ? `${pendingRangeCount} range changes pending · Current results unchanged`
            : 'No range changes'}
        </Typography>
        {pendingRangeCount > 0 && (
          <>
            <Button
              variant="contained"
              color="warning"
              disabled={isSavingRanges}
              onClick={() => void discardRangeChanges()}
            >
              Discard changes
            </Button>
            <Button
              variant="contained"
              color="secondary"
              disabled={!canSaveRanges}
              onClick={() => void saveRangeChanges()}
            >
              Save range changes
            </Button>
          </>
        )}
        <Button
          variant="outlined"
          color="inherit"
          disabled={isSavingRanges}
          onClick={async () => {
            if (!pendingRangeCount || (await discardRangeChanges())) onClose(undefined, true);
          }}
        >
          Close
        </Button>
      </Stack>
      {panelDefinitionParams && (
        <DefineMultiplexPanelsDialog
          casesParams={scope}
          sourceSlideId={panelDefinitionParams.slideId}
          onClose={() => setPanelDefinitionParams(null)}
        />
      )}
    </Stack>
  );
};
